import React, {
    ClipboardEvent,
    FocusEvent,
    forwardRef,
    InputHTMLAttributes,
    MutableRefObject,
    PointerEventHandler,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    PointerEvent,
} from 'react';
import { useHover, useId, useIsSSR } from 'react-aria';
import type { KeyboardEvent } from '@react-types/shared';
import { genericMemo, mergeRefs, noop, useIsomorphicLayoutEffect, useRefCallback } from '@direct-frontend/stdlib';
import { useLogger } from '@direct-frontend/modules-logger';
import { isMobile } from '@direct-frontend/platform-code';

import { useHStack } from '../../hooks/useStack';
import { useObjectRef } from '../../hooks/useObjectRef';
import { getScrollbarClassName } from '../../lib/css/getScrollbarClassName';
import { getCurrentBrowser } from '../../lib/useragent/getCurrentBrowser/getCurrentBrowser';
import { TextArea } from '../TextArea/TextArea';
import { Spinner } from '../Spinner';
import { useTypographyWithSkin } from '../../hooks/useTypography/useTypographyWithSkin';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { useTheme } from '../../lib/theme';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { VStack } from '../Stack/Stack';
import { useFocusOptions } from '../../hooks/useFocusOptions';
import { PseudoInput } from '../PseudoInput/PseudoInput';

import { cls, getColorSkinClass, getHighlightedText } from './utils';
import { ClearButton } from './ClearButton';
import { type InputProps, type TextInputProps, type TextAreaProps, type NumericInputProps } from './types';

import './TextInput.css';

/**
 * Мемоизированный input для предотвращения лишних ререндеров
 */
const MemoizedInput = genericMemo(
    forwardRef<
        HTMLInputElement,
        Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> & Pick<TextInputProps, 'isMaskedInput' | 'value'>
    >(function MemoizedInput(props, ref) {
        const { isMaskedInput, ...inputProps } = props;

        const inputRef = useRef<HTMLInputElement>(null);
        const beforeCaretValueRef = useRef<HTMLDivElement>(null);

        // Получаем значение перед кареткой для отрисовки со стилями инпута

        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        const beforeCaretValue = props.value?.slice(0, inputRef.current?.selectionStart ?? 0 + 1);

        useIsomorphicLayoutEffect(() => {
            // Логика подскролла к курсору в MaskedInput, DIRECT-214709
            if (!isMaskedInput) {
                return;
            }

            const input = inputRef.current;
            const beforeCaretDiv = beforeCaretValueRef.current;

            if (!beforeCaretDiv || !input) {
                return;
            }

            // Если ширина части текста до каретки влезает в инпут, то ничего не делаем
            if (beforeCaretDiv.scrollWidth <= input.clientWidth) {
                return;
            }

            const scrollToWidth = beforeCaretDiv.scrollWidth;

            // Проверяем что конец части текста до каретки лежит в диапазоне от scrollLeft до scrollLeft + clientWidth
            const isCaretVisible =
                scrollToWidth >= input.scrollLeft && scrollToWidth < input.scrollLeft + input.clientWidth;

            if (!isCaretVisible && input.scrollTo) {
                input.scrollTo(scrollToWidth - input.clientWidth + 1, 0);
            }
        }, [props.value]);

        const setInputRef = useMemo(() => mergeRefs(ref, inputRef), [ref]);

        return (
            <>
                <input {...inputProps} ref={setInputRef} />
                {isMaskedInput && (
                    <div className={cls('beforeCaretValue', [props.className])} ref={beforeCaretValueRef}>
                        {beforeCaretValue}
                    </div>
                )}
            </>
        );
    }),
);

// синхронизация прокрутки поля ввода и задника с подсветкой лишних символов
const scrollClass = getScrollbarClassName();

// TODO DIRECT-299822 Выпилить после выхода Safari 18 из использования
const isSafari = getCurrentBrowser() === 'Safari';
const CARET_NAVIGATION_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End']);

/**
 * Очистка строки от спецсимволов HTML
 * @param textContent - HTML-верстка в виде текста
 *
 * @returns textContent - текст с исправленным символом &
 */
export function sanitize(textContent: string): string {
    return textContent.replaceAll('&', '&amp;');
}

/**
 * Компонент для отображения поля ввода
 */
export const TextInput = genericMemo(
    forwardRef<HTMLTextAreaElement | HTMLInputElement, TextInputProps>(function TextInput(props, forwardedRef) {
        const {
            className,
            inputMode = 'text',
            onChange,
            onClear,
            onFocus,
            onBlur,
            onKeyDown,
            onSelect,
            onKeyUp,
            color = 'contour',
            size = 'm',
            isDisabled = false,
            isLoading = false,
            overrideDisabledAddonRight = false,
            validationState,
            placeholder,
            value,
            hasCounter = false,
            minCounterWidth,
            showCounterOnLimitExceededOnly = false,
            hasClearButton = false,
            isRequired = false,
            maxLength,
            addonLeft,
            addonRight,
            highlightRules,
            errorPatterns,
            textAlign = 'left',
            textColor = 'gray',
            maxLengthValueParser,
            autoFocus,
            onHoverStart,
            onHoverChange,
            onHoverEnd,
            onPaste,
            keepFocusOnClearButton,
            autoComplete = 'off',
            inputFieldName,
            'aria-autocomplete': ariaAutocomplete = 'none',
            isMaskedInput,
            spellCheck = false,
        } = props;

        const { LogProvider, logClick } = useLogger('TextInput', props.logNode);

        const disabledState = isDisabled || isLoading;

        // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        const { type = 'text', pattern, width = 'max' } = props as InputProps;
        // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        const { min, max, step } = props as NumericInputProps;
        const {
            isTextarea = false,
            sizeControlType = 'max',
            textAreaRows = 1,
            restrictNewlines = false,
            onPaste: onTextareaPaste,
        } = // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            props as TextAreaProps;

        const inputFieldRef = useObjectRef(forwardedRef);
        const inputFieldId = useId(props.id);
        const autoFocusRef = useRef(autoFocus);

        const sizeSkinClass = getSizeSkin(size);

        const textSize = 'current-main';
        const { textClasses: textTypographyClasses } = useTypographyWithSkin({
            size: textSize,
            color: textColor === 'gray' ? 'current' : textColor,
        });

        const { iconSize, paddingHUnified } = useTheme().sizeTokens[size];
        const { addonClasses: iconAddonAlignClasses } = useInternalVerticalAlignWithSkin({
            textSize,
            addonType: 'icon',
        });

        // управление фокусом и hover-эффектами
        // при взаимодействии с клавиатуры и при ручном фокусировании используются разные модификаторы для стилей
        const { hoverProps, isHovered } = useHover({
            isDisabled: disabledState,
            onHoverStart,
            onHoverChange,
            onHoverEnd,
        });
        // TODO DIRECT-263743 Поресерчить фокусы в модалке на мобиле, после этого если нужно - откатить
        const { focusProps, isFocusVisible, isFocused } = useFocusOptions({ isTextInput: true });

        // Стабилизируем ссылку навсегда
        const onFocusHandler = useRefCallback((event: FocusEvent<HTMLInputElement>) => {
            onFocus?.(event);
            focusProps.onFocus?.(event);
        });

        // Стабилизируем ссылку навсегда
        const onBlurHandler = useRefCallback((event: FocusEvent<HTMLInputElement>) => {
            logClick();
            onBlur?.(event);
            focusProps.onBlur?.(event);
        });

        useEffect(() => {
            if (autoFocusRef.current && inputFieldRef.current) {
                inputFieldRef.current?.focus({
                    // Из-за того, что иногда инпут рисуется внутри попапа,
                    // иногда он не успевает нормально спозиционироваться и скролл происходит не к тому месту
                    preventScroll: true,
                });
            }
        }, [inputFieldRef, autoFocusRef]);

        const backdropRef = useRef<HTMLDivElement>(null);
        const onScrollHandler = useRefCallback(() => {
            // Костыль с requestAnimationFrame для корректной синхронизации скролла в Safari
            // TODO DIRECT-299822 Выпилить после выхода Safari 18 из использования
            requestAnimationFrame(() => {
                if (backdropRef.current && inputFieldRef.current) {
                    backdropRef.current.scrollTop = inputFieldRef.current.scrollTop;
                    backdropRef.current.scrollLeft = inputFieldRef.current.scrollLeft;
                }
            });
        });

        const onKeyDownHandler = useRefCallback((event: KeyboardEvent) => {
            onKeyDown?.(event);

            // В Safari не срабатывает scroll при движении каретки, поэтому проверяем вручную
            // TODO DIRECT-299822 Выпилить после выхода Safari 18 из использования
            if (isSafari && CARET_NAVIGATION_KEYS.has(event.key)) {
                requestAnimationFrame(onScrollHandler);
            }
        });
        const onKeyUpHandler = useRefCallback(onKeyUp ?? noop);
        const onSelectHandler = useRefCallback(onSelect ?? noop);

        // Синхронизация скролла при выделении текста мышью с зажатой ЛКМ
        // TODO DIRECT-299822 Выпилить после выхода Safari 18 из использования
        const onPointerDownHandler = useRefCallback((event: PointerEvent) => {
            event.currentTarget.setPointerCapture(event.pointerId);
        });
        const onPointerMoveHandler = useRefCallback((event: PointerEvent) => {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                onScrollHandler();
            }
        });

        const containerRef = useRef<HTMLDivElement>(null);
        const wrapperRef = useRef<HTMLDivElement>(null);

        // управление очисткой поля ввода данных
        const showClearButton = hasClearButton && Boolean(value);
        const onClearHandler = useCallback(() => {
            if (onClear) {
                onClear();

                return;
            }

            onChange?.('');
        }, [onClear, onChange]);

        // обработка и подсчёт вводимых данных
        const maxValueOptions = maxLengthValueParser && maxLengthValueParser(value || '', maxLength);
        let counterValue: number | undefined;
        let valueMaxLength: number | undefined;

        if (value !== undefined) {
            const valueLength = maxValueOptions ? maxValueOptions.length : value.replace(/\n$/g, '').length;
            counterValue = maxLength ? maxLength - valueLength : undefined;
        }

        if (value && counterValue !== undefined) {
            valueMaxLength = maxValueOptions ? maxValueOptions.overLimitStart : value.length + counterValue;
        }

        const limitExceeded = Boolean(counterValue && counterValue < 0);
        const showCounter =
            maxLength &&
            hasCounter &&
            (!showCounterOnLimitExceededOnly || (showCounterOnLimitExceededOnly && limitExceeded));

        const counterClass = cls(
            'counter',
            minCounterWidth ? { hasMinWidth: true, minWidth: minCounterWidth } : undefined,
        );

        // проверка на то, что текст содержит пробелы (без красных строк) для выбора способа переноса текста
        const shouldBreakWordOfValue = isTextarea && value && /[^\S\r\n]/.test(value);
        const shouldBreakWordOfPlaceholder = isTextarea && placeholder && /[^\S\r\n]/.test(placeholder);
        const shouldBreakWord = value ? shouldBreakWordOfValue : shouldBreakWordOfPlaceholder;

        const onChangeHandler = useRefCallback((event: SyntheticEvent) => {
            // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            const target = event.target as HTMLTextAreaElement | HTMLInputElement;
            const value = target.value;

            onChange?.(value);

            onScrollHandler();
        });

        const onPasteHandler = useRefCallback((event: ClipboardEvent<HTMLInputElement>, value?: string) => {
            onPaste?.(event, value);
        });

        // подсветка текста по правилам из highlightRules и лишних символов при выходе за лимит
        const { textWithHighlight, hasTextErrors } = useMemo(() => {
            if ((highlightRules || errorPatterns) && value && !isLoading) {
                const content = value.slice(0, valueMaxLength);
                return getHighlightedText(highlightRules, errorPatterns, content);
            }

            return {
                textWithHighlight: value && (
                    <span className={cls('mark')} data-content={value.slice(0, valueMaxLength)} />
                ),
                hasTextErrors: false,
            };
        }, [errorPatterns, highlightRules, isLoading, value, valueMaxLength]);

        const textOverLimit = limitExceeded && value && value.slice(valueMaxLength, value.length);
        const hasTextHighlight = Boolean((!disabledState && (highlightRules || errorPatterns)) || textOverLimit);
        const markedText = (textWithHighlight || textOverLimit) && (
            <>
                {textWithHighlight}
                {textOverLimit && <span className={cls('mark', { error: true })} data-content={textOverLimit} />}
            </>
        );

        const hasError = Boolean(!isLoading && (validationState === 'invalid' || limitExceeded || hasTextErrors));

        const dataTestid = props['data-testid'] ?? 'TextInput';

        // создание элемента для ввода данных, все ссылки на прокидываемые пропсы должны быть стабильны
        const inputFieldProps = {
            'aria-required': props['aria-required'] || isRequired,
            'aria-invalid': props['aria-invalid'] || hasError,
            'aria-labelledby': props['aria-labelledby'],
            'aria-errormessage': props['aria-errormessage'],
            'aria-describedby': props['aria-describedby'],
            'aria-autocomplete': ariaAutocomplete,
            'data-testid': dataTestid,

            id: inputFieldId,
            name: inputFieldName,
            disabled: disabledState,

            placeholder,
            value,
            autoComplete: autoComplete,
            spellCheck,

            inputMode: !inputMode && type === 'number' ? 'numeric' : inputMode,
            required: isRequired,

            min,
            max,
            step,

            // Стабильные ссылки на колбэки
            onFocus: onFocusHandler,
            onBlur: onBlurHandler,
            onScroll: onScrollHandler,
            onKeyDown: onKeyDownHandler,
            onKeyUp: onKeyUpHandler,
            onSelect: onSelectHandler,
            onPointerDown: isSafari ? onPointerDownHandler : undefined,
            onPointerMove: isSafari ? onPointerMoveHandler : undefined,
        };

        const inputField = isTextarea ? (
            <TextArea
                {...inputFieldProps}
                // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                ref={inputFieldRef as MutableRefObject<HTMLDivElement>}
                className={cls(
                    'textarea',
                    {
                        textAlign,
                        break: shouldBreakWord ? 'word' : 'all',
                        disabled: disabledState,
                        'with-highlight': hasTextHighlight,
                    },
                    [scrollClass, textTypographyClasses],
                )}
                isDisabled={disabledState}
                textAreaRows={textAreaRows}
                sizeControlType={sizeControlType}
                restrictNewlines={restrictNewlines}
                onChange={onChange}
                onPaste={onTextareaPaste}
            />
        ) : (
            <MemoizedInput
                {...inputFieldProps}
                // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                ref={inputFieldRef as MutableRefObject<HTMLInputElement>}
                className={cls('input', { textAlign, disabled: disabledState, 'with-highlight': hasTextHighlight }, [
                    textTypographyClasses,
                    scrollClass,
                ])}
                type={type}
                pattern={pattern}
                isMaskedInput={isMaskedInput}
                // Должны иметь стабильную ссылку, поэтому обернуты в useRefCallback
                onInput={onChangeHandler}
                onPaste={onPasteHandler}
            />
        );

        const addonLeftComponent = useMemo(() => {
            return addonLeft && <VStack className={cls('addon', [iconAddonAlignClasses])}>{addonLeft}</VStack>;
        }, [addonLeft, iconAddonAlignClasses]);

        const highlighterId = useId();
        const highlighterRef = useRef<HTMLDivElement>(null);

        const isSsr = useIsSSR();

        const getPseudoElementStyles = useCallback(
            (width: number) => {
                if (isSsr) return '';

                return `#${CSS.escape(inputFieldId)}::before,
                #${CSS.escape(highlighterId)}::before {
                    width: ${width ? `calc(${Number(width)}px + var(--dc-size-current-skin-geometry-gap))` : 0};
                }`;
            },
            [highlighterId, inputFieldId, isSsr],
        );

        // обтекание дополнительных элементов (счетчик, кнопка очистки и т.д.)
        const addonContainerRef = useRef<HTMLDivElement>(null);
        const [pseudoElementStyles, setPseudoElementStyles] = useState(getPseudoElementStyles(0));

        const stackClasses = useHStack({
            gap: 'current-geometry',
            justifyContent: 'space-between',
        });

        const addonStackClasses = useHStack({
            gap: 'current-geometry',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
        });

        // дополнительные обтекаемые элементы
        const hasAddons = addonRight || showCounter || showClearButton || isLoading;
        const addonHStackClasses = useHStack({
            alignItems: 'center',
        });
        const addons = (
            <div
                ref={addonContainerRef}
                className={cls('addonContainer', { withTextArea: isTextarea, hidden: !hasAddons }, [addonStackClasses])}
            >
                {isLoading && (
                    <Spinner
                        size={iconSize}
                        color="current"
                        data-testid={props['data-testid'] ? `${props['data-testid']}.Spinner` : 'TextInput.Spinner'}
                    />
                )}

                {addonRight && (
                    <label
                        htmlFor={inputFieldId}
                        className={cls(
                            'addon',
                            { disabled: !overrideDisabledAddonRight && disabledState, addonRight: true },
                            [addonHStackClasses, iconAddonAlignClasses],
                        )}
                    >
                        {addonRight}
                    </label>
                )}

                {showCounter && (
                    <TextGenericWithSkin
                        skinSize={size}
                        textSize={textSize}
                        color={limitExceeded && !disabledState ? 'red' : 'gray-disabled'}
                        className={counterClass}
                        data-testid={props['data-testid'] ? `${props['data-testid']}.Counter` : undefined}
                    >
                        {counterValue}
                    </TextGenericWithSkin>
                )}

                {showClearButton && (
                    <ClearButton
                        size={iconSize}
                        className={cls('clear-button', { disabled: disabledState }, [iconAddonAlignClasses])}
                        onClick={onClearHandler}
                        isDisabled={disabledState}
                        keepFocusOnClearButton={keepFocusOnClearButton}
                        data-testid={dataTestid}
                        logNode={props.logNode}
                    />
                )}
            </div>
        );

        // 1. Поскольку обтекаемый элемент может использовать разную ширину,
        // то ее необходимо вычислить и установить свойство для псевдоэлемента
        //
        // 2. id используется для корректной работы нескольких полей на странице
        const updatePseudoElementsWidth = useRef<() => void>(() => {});
        useIsomorphicLayoutEffect(() => {
            updatePseudoElementsWidth.current = () => {
                if (!addonContainerRef.current || !isTextarea || !hasAddons) {
                    // Обнуляем стили если аддоны пропали
                    if (!hasAddons && pseudoElementStyles !== '') {
                        setPseudoElementStyles(getPseudoElementStyles(0));
                    }

                    return;
                }

                const addonContainerWidth = addonContainerRef.current.offsetWidth;
                typeof window !== 'undefined' && setPseudoElementStyles(getPseudoElementStyles(addonContainerWidth));
            };
        }, [getPseudoElementStyles, hasAddons, highlighterId, inputFieldId, isTextarea, pseudoElementStyles]);

        // исправление смещения при стирании/добавления текста между новыми строками (чтобы не появлялся лишний scroll)
        const updateHighlighterWidth = useRef(() => {});
        useIsomorphicLayoutEffect(() => {
            updateHighlighterWidth.current = () => {
                if (!highlighterRef.current || !inputFieldRef.current || !isTextarea || sizeControlType !== 'auto') {
                    return;
                }

                if (highlighterRef.current.clientWidth < inputFieldRef.current.clientWidth) {
                    highlighterRef.current.style.width = `${inputFieldRef.current.clientWidth}px`;
                }
            };
        }, [inputFieldRef, isTextarea, sizeControlType]);

        // Один раз высчитываем размер скроллбара со стилями
        const updateContainerMargin = useRef(() => {});
        const [containerMarginRight, setContainerMarginRight] = useState<undefined | number>(undefined);
        useIsomorphicLayoutEffect(() => {
            updateContainerMargin.current = () => {
                if (
                    containerMarginRight !== undefined ||
                    !inputFieldRef.current ||
                    !containerRef.current ||
                    // Проверяем что компонент реально отобразился
                    !containerRef.current.clientWidth
                ) {
                    return;
                }

                // Ширина скроллбара
                const scrollbarWidth = containerRef.current.clientWidth - inputFieldRef.current.clientWidth;

                // paddingHUnified должен совпадать с padding-right в css компонента
                const compensationValue = Math.min(scrollbarWidth, paddingHUnified);

                const marginRight = -Math.floor(compensationValue);

                const hasScrollbar = scrollbarWidth > 0;
                if (hasScrollbar) {
                    setContainerMarginRight(marginRight);
                }
            };
        }, [containerMarginRight, inputFieldRef, size]);

        const observer = useMemo(() => {
            if (typeof window === 'undefined' || !window.ResizeObserver) {
                return;
            }

            return new ResizeObserver(entries => {
                for (const entry of entries) {
                    switch (entry.target) {
                        case inputFieldRef.current:
                            updateHighlighterWidth.current();
                            updateContainerMargin.current();
                            break;
                        case addonContainerRef.current:
                            updatePseudoElementsWidth.current();
                            break;
                    }
                }
            });
        }, [inputFieldRef]);

        useEffect(() => {
            // отслеживание размеров контейнеров требуется для предотвращения возможных визуальных багов
            if (inputFieldRef.current) {
                observer?.observe(inputFieldRef.current);
            }

            if (addonContainerRef.current) {
                observer?.observe(addonContainerRef.current);
            }

            return () => observer?.disconnect();
        }, [inputFieldRef, addonContainerRef, observer]);

        // Блокирует скролл бэкдропа, без скроллинга inputField
        useEffect(() => {
            const backdrop = backdropRef.current;

            if (!backdrop) {
                return;
            }

            const handleScrollingEvents = (event: WheelEvent | TouchEvent) => {
                event.preventDefault();
            };

            backdrop.addEventListener('wheel', handleScrollingEvents, { passive: false });
            backdrop.addEventListener('touchmove', handleScrollingEvents, { passive: false });

            return () => {
                backdrop.removeEventListener('wheel', handleScrollingEvents);
                backdrop.removeEventListener('touchmove', handleScrollingEvents);
            };
        }, []);

        // Фокусируемся при клике по контейнеру с паддингами
        const onContainerPointerDown: PointerEventHandler<HTMLDivElement> = useRefCallback(event => {
            if (!inputFieldRef.current || disabledState || event.target !== wrapperRef.current) {
                return;
            }

            event.preventDefault();

            inputFieldRef.current.focus();
        });

        const colorSkinClass = getColorSkinClass(color, {
            isDisabled: disabledState,
            isHovered,
        });

        if (isMobile() && props.isMobileTrigger) {
            return <PseudoInput {...props} onPress={props.onMobileTriggerPress} onClear={onClearHandler} />;
        }

        return (
            <LogProvider>
                <div
                    className={cls(
                        {
                            color,
                            size,
                            autoWidth: width === 'auto',
                            noAddonLeft: !addonLeft,
                            hovered: isHovered,
                            disabled: disabledState,
                            loading: isLoading,
                            focused: isFocused,
                            keyboardFocused: isFocusVisible,
                            hasError: hasError,
                            textarea: isTextarea,
                        },
                        [stackClasses, sizeSkinClass, colorSkinClass, className],
                    )}
                    ref={wrapperRef}
                    onPointerDown={onContainerPointerDown}
                    data-value={value}
                    {...hoverProps}
                >
                    {addonLeftComponent}

                    <div
                        className={cls('container')}
                        ref={containerRef}
                        style={
                            containerMarginRight
                                ? {
                                      marginRight: `${containerMarginRight}px`,
                                  }
                                : undefined
                        }
                    >
                        {pseudoElementStyles && <style>{pseudoElementStyles}</style>}
                        {inputField}
                        <div ref={backdropRef} className={cls('backdrop', [scrollClass])}>
                            <div
                                id={highlighterId}
                                ref={highlighterRef}
                                className={cls(
                                    'highlighter',
                                    {
                                        for: isTextarea ? 'textarea' : 'input',
                                        break: shouldBreakWordOfValue ? 'word' : 'all',
                                        empty: !hasTextHighlight,
                                        textAlign,
                                    },
                                    [textTypographyClasses],
                                )}
                            >
                                {markedText}
                            </div>
                            {isTextarea && addons}
                        </div>
                    </div>
                    {!isTextarea && addons}
                </div>
            </LogProvider>
        );
    }),
);
